import { FileDown } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import type { AdherenceItems, AdherenceStatus } from "@/api/diagnostic";
import { useAdherenceItems } from "@/hooks/useAdherenceItems";
import { useProject } from "@/hooks/useApi";
import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

import type { DiagnosticTabProps } from "../DiagnosticSection";
import { ADHERENCE_BAR, makeAdherenceStatusMeta } from "../diagnosticMeta";
import { FilterButton } from "../orgStructure/FilterButton";
import {
  ADHERENCE_TABS,
  STATUS_CHIPS,
  adherenceCsv,
  adherenceCsvFileName,
  adherenceHeader,
  adherenceLines,
  type AdherenceTab,
} from "./adherenceLines";

/**
 * O drill-down "Aderência ao padrão" (EF-FUNC-011, RF-08 e RF-09), aberto pelo
 * tile e pelo frame do Ato 1.
 *
 * Três abas: Scope items (sem tipo e objeto), Desenvolvimentos (todos os WRICEF,
 * com o scope item standard ou associado e "Conta em") e GAPs, só leitura — o
 * cadastro fica no Agente Functional › Fit-to-Standard › Registro de GAPs. Cada
 * linha diz a cor que recebeu e por quê.
 *
 * Lê o `GET /adherence-index/items`, que calcula com as mesmas fontes do resumo
 * do snapshot. A URL guarda a aba e o chip (`?tab=` e `?status=`), para o link
 * do estado preliminar abrir já em "Sem disposição".
 *
 * A tela vai para o cliente: sem autor, sem quem decidiu, e os GAPs sem o
 * responsável no cliente.
 */
export function AdherenceDetail(_props: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [params, setParams] = useSearchParams();
  const { data, isLoading, refetch } = useAdherenceItems(projectId);
  // O nome vai no arquivo do CSV; a página já leu o projeto, então vem do cache.
  const { data: project } = useProject(projectId);

  const tab = ADHERENCE_TABS.find((known) => known === params.get("tab")) ?? "scope-items";
  const status = STATUS_CHIPS[tab].find((known) => known === params.get("status")) ?? null;
  const lines = useMemo(() => (data ? adherenceLines(tab, data, t) : []), [data, tab, t]);
  const shown = status ? lines.filter((entry) => entry.status === status) : lines;

  function choose(next: { tab: AdherenceTab; status: AdherenceStatus | null }) {
    const search: Record<string, string> = { tab: next.tab };
    if (next.status) search.status = next.status;
    setParams(search, { replace: true });
  }

  function exportCsv() {
    const blob = new Blob([adherenceCsv(tab, shown, t)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = adherenceCsvFileName(tab, project?.name, new Date());
    anchor.click();
    URL.revokeObjectURL(url);
  }

  if (isLoading) {
    return <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>;
  }
  if (!data) {
    // Falha não é lista vazia: "nenhum item" afirmaria algo sobre o projeto.
    return (
      <div
        data-testid="adherence-detail-error"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
      >
        {t("Could not load the adherence items.")}
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
        >
          {t("Try again")}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#e3e8ef] bg-white">
      <div className="flex flex-wrap items-start gap-3 border-b border-[#eef1f5] p-4">
        <p className="flex-1 text-[13px] text-[#596b85]">
          {t("Each line shows the colour it got and why.")}
        </p>
        <button
          type="button"
          onClick={exportCsv}
          disabled={shown.length === 0}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[#d7dde6] bg-white px-3 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FileDown aria-hidden className="h-3.5 w-3.5" />
          {t("Export CSV")}
        </button>
      </div>

      <Tabs data={data} tab={tab} onChoose={(next) => choose({ tab: next, status: null })} />

      <div className="flex flex-col gap-3 p-4">
        <p className="text-[12px] leading-relaxed text-[#596b85]">{tabNote(tab, t)}</p>
        {STATUS_CHIPS[tab].length > 0 && (
          <StatusChips
            tab={tab}
            counts={countByStatus(lines)}
            total={lines.length}
            active={status}
            onChoose={(next) => choose({ tab, status: next })}
          />
        )}
        <LinesTable
          tab={tab}
          lines={shown}
          emptyLabel={lines.length === 0 ? t("No items.") : t("No items for the selected filters.")}
        />
      </div>
    </div>
  );
}

function tabNote(tab: AdherenceTab, t: (key: string) => string): string {
  if (tab === "scope-items") {
    return t(
      "Validated scope items (“Activate” in the curation) and those still under decision. Green: met 100% by the standard. Yellow: the standard complemented by an existing development that will be converted. Red: GAP.",
    );
  }
  if (tab === "developments") {
    return t(
      "All the developments in the WRICEF catalog. “Covered by standard”: the development will not be converted and must be assessed in the indicated standard scope item. Those associated with a scope item count inside it (“Counts in” column). Out of the count at the end of the list.",
    );
  }
  return t("Read only. The register lives in the Functional agent › Fit-to-Standard › GAP register.");
}

function countByStatus(lines: { status: AdherenceStatus | null }[]): Partial<Record<AdherenceStatus, number>> {
  const counts: Partial<Record<AdherenceStatus, number>> = {};
  for (const entry of lines) {
    if (entry.status) counts[entry.status] = (counts[entry.status] ?? 0) + 1;
  }
  return counts;
}

function Tabs({
  data,
  tab,
  onChoose,
}: {
  data: AdherenceItems;
  tab: AdherenceTab;
  onChoose: (tab: AdherenceTab) => void;
}) {
  const { t } = useTranslation();
  const labels: Record<AdherenceTab, [string, number]> = {
    "scope-items": [t("Scope items"), data.scope_items.length],
    developments: [t("Developments"), data.developments.length],
    gaps: [t("GAPs"), data.gaps.length],
  };
  return (
    <div role="tablist" className="flex gap-1 border-b border-[#eef1f5] px-4">
      {ADHERENCE_TABS.map((key) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={tab === key}
          onClick={() => onChoose(key)}
          className={cn(
            "-mb-px border-b-2 px-3 py-2 text-[13px] font-medium",
            tab === key
              ? "border-[#08254c] text-[#08254c]"
              : "border-transparent text-[#596b85] hover:text-[#08254c]",
          )}
        >
          {labels[key][0]}{" "}
          <span className="rounded-full bg-[#eef1f5] px-1.5 text-[11px] text-[#3c4b61]">
            {formatCount(labels[key][1])}
          </span>
        </button>
      ))}
    </div>
  );
}

function StatusChips({
  tab,
  counts,
  total,
  active,
  onChoose,
}: {
  tab: AdherenceTab;
  counts: Partial<Record<AdherenceStatus, number>>;
  total: number;
  active: AdherenceStatus | null;
  onChoose: (status: AdherenceStatus | null) => void;
}) {
  const { t } = useTranslation();
  const labels = makeAdherenceStatusMeta(t);
  return (
    <div className="flex flex-wrap gap-2">
      <FilterButton testId="adherence-filter-all" active={active === null} onClick={() => onChoose(null)}>
        {t("All")} {formatCount(total)}
      </FilterButton>
      {STATUS_CHIPS[tab].map((status) => (
        <FilterButton
          key={status}
          testId={`adherence-filter-${status}`}
          active={active === status}
          onClick={() => onChoose(status)}
        >
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className={cn("h-2.5 w-2.5 rounded-sm", ADHERENCE_BAR[status])} />
            {labels[status]} {formatCount(counts[status] ?? 0)}
          </span>
        </FilterButton>
      ))}
    </div>
  );
}

function LinesTable({
  tab,
  lines,
  emptyLabel,
}: {
  tab: AdherenceTab;
  lines: ReturnType<typeof adherenceLines>;
  emptyLabel: string;
}) {
  const { t } = useTranslation();
  const labels = makeAdherenceStatusMeta(t);
  const header = adherenceHeader(tab, t);
  const withStatus = tab !== "gaps";
  const columns = header.length + (withStatus ? 1 : 0);
  return (
    <div className="overflow-x-auto">
      <table data-testid="adherence-table" className="w-full min-w-[960px] border-collapse text-left">
        <thead>
          <tr className="border-b border-[#eef1f5] font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
            {withStatus && <th className="px-3 py-2 font-medium">{t("Status")}</th>}
            {header.map((title) => (
              <th key={title} className="px-3 py-2 font-medium">
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((entry) => (
            <tr
              key={entry.key}
              data-testid={`adherence-line-${entry.key}`}
              className="border-b border-[#eef1f5] align-top text-[12px] text-[#3c4b61] last:border-b-0"
            >
              {withStatus && entry.status && (
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium text-[#08254c]">
                    <span aria-hidden className={cn("h-2.5 w-2.5 rounded-sm", ADHERENCE_BAR[entry.status])} />
                    {labels[entry.status]}
                  </span>
                </td>
              )}
              {entry.cells.map((cell, position) => (
                <td
                  key={position}
                  className={cn(
                    "max-w-[320px] whitespace-pre-line px-3 py-2",
                    position === 0 && "font-mono font-semibold text-[#08254c]",
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
          {lines.length === 0 && (
            <tr>
              <td colSpan={columns} className="px-3 py-6 text-center text-[12px] text-[#7b8799]">
                {emptyLabel}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
